import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Video, MapPin, X, Camera, ShieldAlert, AlertTriangle, Send, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import LocationPicker from "@/components/LocationPicker";

export default function LiveComplaint() {
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", anonymous: false });
  const [video, setVideo] = useState(null);          // base64 data-url
  const [photos, setPhotos] = useState([]);          // array of base64
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feed, setFeed] = useState([]);
  // Phase 3 polish · explicit user-picked location beats the shared
  // GPS-driven LocationContext. `pinnedLoc` is set by the LocationPicker
  // (search / map / GPS-in-the-picker). A subsequent LocationContext
  // GPS refresh must NEVER clear this.
  const [pinnedLoc, setPinnedLoc] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    api.get("/complaints/types").then(r => setTypes(r.data || [])).catch(() => {});
    loadFeed();
  }, []);

  const loadFeed = async () => {
    try {
      const params = loc?.lat ? `?lat=${loc.lat}&lng=${loc.lng}&radius_km=50` : "";
      const r = await api.get(`/complaints${params}`);
      setFeed(r.data || []);
    } catch {}
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: true });
      streamRef.current = stream;
      if (previewRef.current) previewRef.current.srcObject = stream;
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm" });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        if (blob.size > 6_000_000) { toast.error("Clip too long — keep under 15 seconds"); return; }
        const r = new FileReader();
        r.onload = () => setVideo(r.result);
        r.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
      // Auto-stop at 15 seconds to keep payload small.
      setTimeout(() => { try { mr.state === "recording" && mr.stop(); setRecording(false); } catch {} }, 15_000);
    } catch (e) {
      toast.error("Camera access denied or unavailable");
    }
  };
  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.stop();
      setRecording(false);
    } catch {}
  };

  const pickPhoto = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    files.forEach(f => {
      if (f.size > 3_000_000) { toast.error("Photo too large (max 3MB)"); return; }
      const r = new FileReader();
      r.onload = () => setPhotos(p => [...p, r.result].slice(0, 4));
      r.readAsDataURL(f);
    });
  };

  const submit = async () => {
    if (!selectedType) { toast.error("Pick a complaint type"); return; }
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    // Phase 3 · effective location — explicit user pick beats GPS.
    const eff = pinnedLoc && pinnedLoc.lat != null
                ? pinnedLoc
                : (loc && loc.lat != null ? loc : null);
    if (!eff?.lat || !eff?.lng) {
      toast.error("Location required — tap 'Use current location' or 'Choose on map'");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        complaint_type: selectedType,
        title: form.title,
        description: form.description,
        severity: form.severity,
        anonymous: form.anonymous,
        lat: eff.lat, lng: eff.lng,
        city: eff.city || "",
        video: video || undefined,
        photos,
      };
      await api.post("/complaints", payload);
      toast.success("Complaint filed");
      setSelectedType(""); setForm({ title: "", description: "", severity: "medium", anonymous: false });
      setVideo(null); setPhotos([]);
      loadFeed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="complaint-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="text-red-600" size={24} />
        <h1 className="heading text-2xl font-black text-stone-900">Live Complaint</h1>
      </div>
      <p className="text-xs text-stone-500 mb-4">Record a 15-second video with your location auto-tagged. Choose a category below.</p>

      {/* Types grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        {types.map(t => {
          const active = selectedType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setSelectedType(t.key)}
              className={`text-left rounded-2xl p-3 border-2 text-xs font-bold transition-all ${active ? "border-red-500 bg-red-50 text-red-700" : "border-stone-200 hover:border-stone-400 text-stone-700 bg-white"}`}
              data-testid={`complaint-type-${t.key}`}
            >
              <div className="text-[11px]">{t.label}</div>
            </button>
          );
        })}
      </div>

      {selectedType && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
          <Input placeholder="Short title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="complaint-title" />
          <Textarea placeholder="What happened? (optional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="complaint-desc" />

          {/* Video capture */}
          <div className="bg-stone-50 rounded-xl p-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1"><Video size={12} /> Video evidence (auto-stops in 15s)</div>
            {!video ? (
              <div>
                <video ref={previewRef} autoPlay muted className={`w-full rounded-lg bg-black ${recording ? "block" : "hidden"}`} style={{ maxHeight: 240 }} />
                {!recording ? (
                  <Button onClick={startRecording} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="complaint-record-btn">
                    <Video size={14} className="mr-1.5" /> Start recording
                  </Button>
                ) : (
                  <Button onClick={stopRecording} className="rounded-full bg-stone-800 text-white" data-testid="complaint-stop-btn">
                    Stop
                  </Button>
                )}
              </div>
            ) : (
              <div className="relative">
                <video src={video} controls className="w-full rounded-lg" style={{ maxHeight: 240 }} />
                <button onClick={() => setVideo(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white grid place-items-center"><X size={14} /></button>
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="bg-stone-50 rounded-xl p-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1"><Camera size={12} /> Photos (up to 4)</div>
            <div className="flex gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img loading="lazy" src={p} alt="" className="w-16 h-16 object-cover rounded-lg" />
                  <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black text-white grid place-items-center text-[10px]">×</button>
                </div>
              ))}
              {photos.length < 4 && (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-stone-300 grid place-items-center text-stone-400 hover:text-stone-700 cursor-pointer">
                  <Camera size={20} />
                  <input type="file" accept="image/*" multiple hidden onChange={pickPhoto} data-testid="complaint-photo-input" />
                </label>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} className="border border-stone-300 rounded-lg px-3 py-2 text-sm">
              <option value="low">Low severity</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-stone-600">
              <input type="checkbox" checked={form.anonymous} onChange={e => setForm({ ...form, anonymous: e.target.checked })} data-testid="complaint-anon" />
              File anonymously
            </label>
          </div>

          {/* Phase 3 · worldwide location picker — user can file a
               complaint about a location they aren't physically at
               (visiting a neighbour, remote observation, etc.). GPS is
               never required. */}
          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3">
            <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 mb-2">
              Complaint location
            </div>
            <LocationPicker
              value={pinnedLoc?.lat != null
                       ? pinnedLoc
                       : (loc?.lat != null ? { ...loc, source: "gps" } : null)}
              onChange={(next) => setPinnedLoc({
                lat:  Number(Number(next.lat).toFixed(6)),
                lng:  Number(Number(next.lng).toFixed(6)),
                city: next.city || loc?.city,
                source: next.source || "manual",
              })}
            />
          </div>

          <div className="flex items-center justify-between border-t border-stone-100 pt-3">
            <div className="text-[11px] text-stone-500 inline-flex items-center gap-1" data-testid="complaint-loc-label">
              <MapPin size={11} />
              {(() => {
                const eff = pinnedLoc?.lat != null ? pinnedLoc : loc;
                return (eff?.city || "Location required") +
                       (eff?.lat != null ? ` · ${eff.lat.toFixed(3)}, ${eff.lng.toFixed(3)}` : " · …");
              })()}
            </div>
            <Button
              onClick={submit}
              disabled={submitting || !form.title.trim()
                        || !((pinnedLoc?.lat != null) || (loc?.lat != null))}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="complaint-submit"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Filing…</> : <><Send size={14} className="mr-1.5" /> File complaint</>}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Nearby feed */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1">
          <AlertTriangle size={11} /> Nearby complaints ({feed.length})
        </div>
        <div className="space-y-2">
          {feed.map(c => (
            <div key={c.complaint_id} className="bg-white border border-stone-200 rounded-xl p-3 text-sm" data-testid={`complaint-feed-${c.complaint_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{c.complaint_type.replace(/_/g, " ")}</span>
                {c.has_video && <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5"><Video size={9} /> Video</span>}
                {typeof c.distance_km === "number" && <span className="text-[10px] text-stone-500">{c.distance_km} km</span>}
                {c.status !== "open" && <span className="text-[10px] font-bold text-stone-500">· {c.status}</span>}
              </div>
              <div className="font-bold text-stone-900 mt-1">{c.title}</div>
              <div className="text-xs text-stone-500 line-clamp-2">{c.description}</div>
              <div className="text-[10px] text-stone-400 mt-1 inline-flex items-center gap-1"><MapPin size={9} /> {c.city}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
